import { TRPCClientError } from "@trpc/client"
import type { MessagesHostContract, MessagesProviderApi } from "@internal/messages"
import { carrotClient } from "#/utils/CarrotClient"
import { trpc } from "#/lib/trpc/client"

// The transport half of the messages host contract. `carrotClient` is a module singleton, so this
// never changes identity — built once for the lifetime of the tab rather than per render.
//
// It lives in its own module, apart from `page.tsx`, because the nav's unread dot needs
// `getThreads` too and importing it from the page would drag `PageLayout`/`PageHeader` and the
// rest of the page's chrome into the nav's bundle.
export const api: MessagesHostContract["api"] = {
  getThreads: () => carrotClient.getInAppMessagingThreads(),
  getThreadMessages: (threadId) => carrotClient.getThreadMessages(threadId),
  createThread: (subject, message, viaFollowUpSourceId) =>
    carrotClient.createThread(subject, message, viaFollowUpSourceId),
  updateThread: (threadId, message) => carrotClient.updateThread(threadId, message),
  patchThread: (threadId, payload) => carrotClient.patchThread(threadId, payload),
  getCurrentUser: () => carrotClient.getCurrentUser(),
  getCarrotPlanMessage: () => carrotClient.getCarrotCompanionCarrotPlanMessage()
}

/**
 * The ONE piece of translation the provider adapter does, and the reason it exists at all.
 *
 * The messages package decides retries and terminal failures by reading a numeric `statusCode` off
 * the rejection — the shape `ResponseError` has on the carrot path (`ProviderApiError` in the
 * package). A `TRPCClientError` puts the upstream status somewhere else entirely,
 * `error.data.httpStatus`, so handing one straight through leaves `statusCode` undefined and every
 * retry switch in the package falls to its default branch. A 403 on someone else's conversation, a
 * 409 on a closed thread and a 412 for a member with no Healthie identity would all be retried four
 * times before surfacing, and the package's terminal-error handling would silently stop working.
 *
 * Deliberately NO retry here. The package owns read retries (see `useProviderThreadsQuery` and
 * `useThreadMessages`), and the send has none by design: Healthie's `createNote` takes no
 * idempotency key at the pinned API version and a note attributed to the member cannot be deleted
 * with our credential, so a retried send posts a permanent duplicate into a real clinical
 * conversation.
 */
async function withStatusCode<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    if (error instanceof TRPCClientError) {
      const statusCode = (error.data as { httpStatus?: number } | undefined)?.httpStatus
      // The original is kept as `cause` so Sentry still reports the tRPC path, shape and message.
      throw Object.assign(new Error(error.message, { cause: error }), { statusCode })
    }
    // Anything else (an aborted fetch, a torn socket) has no upstream status to attach and is
    // already the error the caller should see.
    throw error
  }
}

// The PROVIDER (Healthie) transport, reached through the BFF over tRPC rather than the Carrot API.
//
// Handed to the package unconditionally by both host components (`page.tsx` and
// `ConsumerUnreadMessagesDot.tsx`) — there is no flag. A member with no Healthie conversations gets
// an empty list, which is indistinguishable from the feature not existing. This module just builds
// the adapter.
export const providerApi: MessagesProviderApi = {
  // UNPAGED, both reads: the procedures answer the member's whole inbox and a thread's whole
  // history out of the Convex mirror, exactly as the legacy Carrot list does. `threadList` takes
  // no input at all.
  getThreads: () => withStatusCode(() => trpc.messages.threadList.query()),
  getThreadMessages: ({ threadId }) => withStatusCode(() => trpc.messages.threadMessages.query({ threadId })),
  sendMessage: ({ threadId, body, clientMessageId }) =>
    withStatusCode(() => trpc.messages.sendThreadMessage.mutate({ threadId, body, clientMessageId })),
  // The procedure answers `{ threadId, unread }`, which the contract does not promise and no caller
  // reads. Discarded rather than widening `markRead` to return it: the package would then be able to
  // depend on a payload the next transport may not have.
  markRead: async ({ threadId }) => {
    await withStatusCode(() => trpc.messages.setThreadRead.mutate({ threadId }))
  }
}
